import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { MSSQL_OPENJSON_COMPAT_LEVEL } from '../../hooks/useMssqlCompatLevel';

/**
 * Discriminator for what's wrong with a JSONPath input. `starts-with-$`
 * covers the missing-root and unbalanced-bracket cases;
 * `recursive-descent-unsupported` is engine-aware (Slice 13) — flagged
 * only on engines without native `..` support; `unsupported-syntax`
 * covers filter expressions (Slice 14) the SQL emitter can't lower yet;
 * `mssql-low-compat-wildcard` covers MSSQL databases at compat level
 * below 130 where `OPENJSON` (used for every `[*]` walk) is unavailable.
 * `[*]` array wildcards are supported as of Slice 12 and no longer
 * flagged unconditionally. `null` means valid — no hint shown.
 */
export type JsonPathHint =
  | 'starts-with-$'
  | 'unsupported-syntax'
  | 'recursive-descent-unsupported'
  | 'mssql-low-compat-wildcard'
  | 'kusto-wildcard-unsupported';

export const JSON_PATH_HINT_TEXT: Record<JsonPathHint, string> = {
  'starts-with-$': '(starts with $)',
  'unsupported-syntax': 'filter expressions not yet supported',
  'recursive-descent-unsupported':
    'Recursive descent (..) not supported on this engine',
  'mssql-low-compat-wildcard':
    'Wildcard ([*]) paths require database compatibility level 130 or higher',
  'kusto-wildcard-unsupported':
    'Array wildcard ([*]) paths are not supported for Azure Data Explorer',
};

/** Engines that emit native SQL for recursive-descent (`..`) paths. */
const RECURSIVE_DESCENT_SUPPORTED: ReadonlyArray<DatabaseEngineId> = [
  'pgsql',
  'oracle',
];

/**
 * Optional context for engine-aware path validation. `mssqlCompatLevel`
 * comes from `useMssqlCompatLevel` and is read in main from
 * `sys.databases.compatibility_level`; it gates the `[*]` warning on
 * MSSQL only when we positively know the level is below 130. Unknown
 * (null/undefined) is treated as "don't block" — Layer 2 catches the
 * server-side error if our pre-apply check was too lenient.
 */
export type JsonPathValidatorContext = {
  engine?: DatabaseEngineId;
  mssqlCompatLevel?: number | null;
};

/**
 * Lightweight syntax validator for the JSONPath input. Returns the hint
 * kind to surface, or `null` when the input is valid (or empty). Catches:
 *
 * - missing `$` root or leading `[`,
 * - recursive descent (`..`) — flagged only on engines without native
 *   support (MySQL / MSSQL / SQLite); accepted on PG and Oracle,
 * - `[*]` wildcards on MSSQL when the database is at compat level below
 *   130 (OPENJSON unavailable),
 * - filter expressions (`[?...]`) — soft-rejected until Slice 14,
 * - unbalanced brackets / parens.
 *
 * `[*]` array wildcards are accepted (Slice 12) on every engine except
 * the MSSQL low-compat case above. Intentionally not a parser. Don't
 * block submit — just hint. Accepts either a single `DatabaseEngineId`
 * (legacy callers, recursive-descent only) or a `JsonPathValidatorContext`
 * (Layer 1 callers that also need the compat-level check).
 */
export function getJsonPathHint(
  raw: string | null | undefined,
  engineOrCtx?: DatabaseEngineId | JsonPathValidatorContext,
): JsonPathHint | null {
  const ctx: JsonPathValidatorContext =
    typeof engineOrCtx === 'string' || engineOrCtx === undefined
      ? { engine: engineOrCtx }
      : engineOrCtx;
  const { engine, mssqlCompatLevel } = ctx;
  const v = (raw ?? '').trim();
  if (v.length === 0) return null;
  if (v.startsWith('[')) return 'starts-with-$';
  if (!v.startsWith('$')) return 'starts-with-$';
  // `..` anywhere in the path means recursive descent (Slice 13). On
  // engines that support it natively, fall through to other checks; on
  // unsupported engines (or unknown engine), flag with the engine-
  // specific hint.
  if (v.includes('..') && !(engine && RECURSIVE_DESCENT_SUPPORTED.includes(engine))) {
    return 'recursive-descent-unsupported';
  }
  // Kusto has no JSONPath array wildcard — the KQL emitter can't lower `[*]`
  // (and the any/all aggregate sub-ops it enables). Warn before the user
  // applies a wildcard path that the cluster would reject.
  if (engine === 'kusto' && v.includes('[*]')) {
    return 'kusto-wildcard-unsupported';
  }
  // `[*]` on a MSSQL database with compat < 130 means OPENJSON would
  // fail at the binder (Msg 208). Surface this before the user clicks
  // Apply so they can either bump compat or drop the wildcard.
  if (
    engine === 'mssql' &&
    typeof mssqlCompatLevel === 'number' &&
    mssqlCompatLevel < MSSQL_OPENJSON_COMPAT_LEVEL &&
    v.includes('[*]')
  ) {
    return 'mssql-low-compat-wildcard';
  }
  // Filter expressions `[?(...)]` are Slice 14. `[*]` array wildcards
  // are supported as of Slice 12 and no longer flagged.
  if (v.includes('[?')) return 'unsupported-syntax';
  let bracket = 0;
  let paren = 0;
  for (const ch of v) {
    if (ch === '[') bracket++;
    else if (ch === ']') bracket--;
    else if (ch === '(') paren++;
    else if (ch === ')') paren--;
    if (bracket < 0 || paren < 0) return 'starts-with-$';
  }
  return bracket !== 0 || paren !== 0 ? 'starts-with-$' : null;
}

/**
 * Boolean wrapper around `getJsonPathHint` — kept for callers that only
 * need a yes/no signal (e.g. `aria-invalid`). Prefer `getJsonPathHint`
 * when you also need to render hint text.
 */
export function isJsonPathLikelyMalformed(
  raw: string | null | undefined,
  engineOrCtx?: DatabaseEngineId | JsonPathValidatorContext,
): boolean {
  return getJsonPathHint(raw, engineOrCtx) !== null;
}
